import { PermissionEnum } from '@Application/Security/Roles';
import UpdateDocumentTemplateVersionSections from '@Application/Documents/Templates/Commands/UpdateDocumentTemplateVersionSections';
import updateDocumentTemplateVersionSections from '@Application/Documents/Templates/updateDocumentTemplateVersionSections';
import ReevaluationSectionCheckbox from '@UI/Pages/DocumentTemplates/Shared/Declaration/ReevaluationSectionCheckbox';
import DeclarationGroup from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationGroup';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type DocumentTemplateVersionDetailView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionDetailView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, Message, Tag } from '@verclam/ui-kit';

const treeStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const sectionsBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-bottom: 0.75rem;
`;

const sectionRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly version: DocumentTemplateVersionDetailView;
};

/** Read-only rendering of an already-deposited version's declaration: the server already resolved
 * every field's type label, with a repli built in — nothing left to fall back on here. */
const DocumentTemplateVersionDeclaration: React.FC<Props> = ({ version }) => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const [selectedKeys, setSelectedKeys] = React.useState<readonly string[]>(
		version.reevaluationSectionKeys,
	);
	const [isSaving, setIsSaving] = React.useState(false);

	React.useEffect(() => {
		setSelectedKeys(version.reevaluationSectionKeys);
	}, [version]);

	const topLevelNodes = React.useMemo(() => {
		return version.nodes.filter((node) => !node.path.includes('.'));
	}, [version.nodes]);

	const hasChanged = React.useMemo(() => {
		return (
			selectedKeys.length !== version.reevaluationSectionKeys.length ||
			selectedKeys.some((key) => !version.reevaluationSectionKeys.includes(key))
		);
	}, [selectedKeys, version.reevaluationSectionKeys]);

	const handleToggle = React.useCallback((key: string) => {
		setSelectedKeys((current) =>
			current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
		);
	}, []);

	const handleSave = React.useCallback(() => {
		if (currentOrganization === null) {
			return;
		}

		setIsSaving(true);

		dispatch(
			updateDocumentTemplateVersionSections(
				new UpdateDocumentTemplateVersionSections(currentOrganization.id, version.id, selectedKeys),
			),
		)
			.catch(ignoreHandledError)
			.finally(() => setIsSaving(false));
	}, [currentOrganization, dispatch, version.id, selectedKeys]);

	if (version.fieldCount === 0) {
		return <Message severity="info" text="Cette version ne déclare aucune variable." />;
	}

	return (
		<React.Fragment>
			<div css={sectionsBlockStyle}>
				{topLevelNodes.length === 0 ? (
					<Message severity="info" text="Ce gabarit ne déclare aucune section marquable." />
				) : (
					<WithPermission
						permission={PermissionEnum.DOCUMENT_TEMPLATE_WRITE}
						fallback={topLevelNodes
							.filter((node) => version.isReevaluationSection(node.uuid))
							.map((node) => (
								<div key={node.uuid} css={sectionRowStyle}>
									<span>{node.name}</span>
									<Tag value="Réévaluation" severity="info" />
								</div>
							))}
					>
						{topLevelNodes.map((node) => (
							<div key={node.uuid} css={sectionRowStyle}>
								<span>{node.name}</span>
								<ReevaluationSectionCheckbox
									sectionKey={node.uuid}
									checked={selectedKeys.includes(node.uuid)}
									onToggle={handleToggle}
								/>
							</div>
						))}
						<Button
							type="button"
							label="Enregistrer les sections de réévaluation"
							loading={isSaving}
							disabled={!hasChanged}
							onClick={handleSave}
						/>
					</WithPermission>
				)}
			</div>
			<div css={treeStyle}>
				{version.sections.map((section) => (
					<DeclarationGroup key={section.key} group={section} />
				))}
			</div>
		</React.Fragment>
	);
};

export default DocumentTemplateVersionDeclaration;
